import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { DropdownField, RadioCardField } from '@frontend/design-system';
import { AvailabilityFormRow } from '../../availability-form/atoms/availability-form-row';
import { AvailabilityFormSection } from '../../availability-form/atoms/availability-form-section';
import { PreviewNotification } from '../../availability-form/atoms/preview-notification';
import { RadioCardFieldRow } from '../../availability-form/atoms/radio-card-field-row';
import {
  BookingCadenceOptions,
  bookingCadenceOptions,
  GetAvailabilityRuleFormFieldProps,
  patientBookingOptions,
} from '../../availability-form/types';
import {
  convertTimeToMinutes,
  getCadencePreviewDescriptionForRange,
  getSelectedServiceDuration,
} from '../../helpers/booking-rules-form-helpers';

type BookingRulesFormProps = {
  locationId: string | undefined;
  getFieldProps: GetAvailabilityRuleFormFieldProps;
};

export const BookingRulesForm = ({ locationId, getFieldProps }: BookingRulesFormProps) => {
  const { t } = useTranslation('schedule-settings');
  const bookingCadenceFieldProps = getFieldProps('bookingCadence');
  const servicesFieldProps = getFieldProps('services');
  const startTime = getFieldProps('startTime') ?? '';
  const endTime = getFieldProps('endTime') ?? '';

  const serviceTypesQuery = SchedulerV3.Helpers.useGetServiceTypesV3Data({
    selectedLocationId: locationId || '',
  });

  const selectedServiceDuration = useMemo(() => {
    return getSelectedServiceDuration({
      appointmentTypeGroups: serviceTypesQuery.data?.appointmentTypeGroups ?? [],
      selectedServiceIds: servicesFieldProps.value ?? [],
    });
  }, [serviceTypesQuery.data?.appointmentTypeGroups, servicesFieldProps]);

  const previewNotificationDescription = useMemo(() => {
    const startMinutes = convertTimeToMinutes(startTime?.value);
    const endMinutes = convertTimeToMinutes(endTime?.value);
    const cadenceId = bookingCadenceFieldProps.value as BookingCadenceOptions | undefined;

    if (
      !selectedServiceDuration ||
      !cadenceId ||
      startMinutes === null ||
      endMinutes === null ||
      endMinutes <= startMinutes
    ) {
      return null;
    }

    return getCadencePreviewDescriptionForRange({
      cadenceId,
      startMinutes,
      endMinutes,
      serviceDuration: selectedServiceDuration,
    });
  }, [bookingCadenceFieldProps.value, startTime, endTime, selectedServiceDuration]);

  return (
    <AvailabilityFormSection title={t('Booking Rules')} hasBottomBorder={false}>
      <AvailabilityFormRow>
        <DropdownField label={t('Booking Cadence')} {...bookingCadenceFieldProps} hasNonFloatingLabel>
          {bookingCadenceOptions?.map((item) => (
            <DropdownField.Option key={item.id} value={item.id}>
              {item.displayName}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </AvailabilityFormRow>
      {previewNotificationDescription && (
        <AvailabilityFormRow>
          <PreviewNotification title={t('Cadence preview:')} description={previewNotificationDescription} />
        </AvailabilityFormRow>
      )}
      <AvailabilityFormRow>
        <RadioCardFieldRow label={t('Patient Booking')} {...getFieldProps('patientBooking')} hasNonFloatingLabel>
          {patientBookingOptions?.map((item) => (
            <RadioCardField.Option key={item.id} value={item.id} title={item.title} description={item.description} />
          ))}
        </RadioCardFieldRow>
      </AvailabilityFormRow>
    </AvailabilityFormSection>
  );
};
